import { getLenis } from '../hooks/useLenis';

const HOME_SCROLL_STORAGE_KEY = 'zl-home-scroll-to';
const TOPBAR_HEIGHT = 72;
const SECTION_SCROLL_OFFSET = -(TOPBAR_HEIGHT + 16);
/** Menu nav on home — extra scroll past section top */
const SECTION_MENU_EXTRA_OFFSET = 80;
/** Extra when returning from a subpage (Zurück / menu → home section) */
const SECTION_BACK_EXTRA_OFFSET = 80;

/** Section anchor scroll — longer glide for menu / deep links */
const SECTION_SCROLL_DURATION_MS = 780;

export function setPendingHomeScroll(sectionId: string) {
  sessionStorage.setItem(HOME_SCROLL_STORAGE_KEY, sectionId);
}

export function consumePendingHomeScroll(): string | null {
  const id = sessionStorage.getItem(HOME_SCROLL_STORAGE_KEY);
  if (id) sessionStorage.removeItem(HOME_SCROLL_STORAGE_KEY);
  return id;
}

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth < 769;

/** Soft ease-out — long gentle deceleration into target */
export function scrollEaseOut(t: number): number {
  return 1 - Math.pow(1 - t, 2.4);
}

function animateScrollToY(targetTop: number, duration: number) {
  const start = window.scrollY;
  const distance = targetTop - start;
  if (Math.abs(distance) < 2) return;

  let startTime: number | null = null;

  function step(timestamp: number) {
    if (startTime === null) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = scrollEaseOut(progress);
    window.scrollTo(0, start + distance * eased);
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

/**
 * Smooth-scrolls to a target element with a controlled duration.
 * Uses Lenis when active (desktop), same easing curve on mobile via rAF.
 */
export function smoothScrollTo(
  el: HTMLElement,
  options?: { duration?: number; offset?: number },
) {
  const { duration = 400, offset = 0 } = options ?? {};
  const lenis = getLenis();

  if (lenis) {
    lenis.scrollTo(el, {
      offset,
      duration: duration / 1000,
      easing: scrollEaseOut,
    });
    return;
  }

  const targetTop = el.getBoundingClientRect().top + window.scrollY + offset;
  animateScrollToY(Math.max(0, targetTop), duration);
}

/** Scroll to a section by id, retrying until the element exists (e.g. after route change). */
export function scrollToSectionById(
  sectionId: string,
  options?: { duration?: number; offset?: number; initialDelay?: number },
) {
  const mobile = isMobileViewport();
  const {
    initialDelay = mobile ? 680 : 400,
    duration = SECTION_SCROLL_DURATION_MS,
    offset = SECTION_SCROLL_OFFSET + SECTION_MENU_EXTRA_OFFSET,
  } = options ?? {};

  const tryScroll = (attempts: number) => {
    const el = document.getElementById(sectionId);
    if (el) {
      smoothScrollTo(el, { duration, offset });
    } else if (attempts > 0) {
      setTimeout(() => tryScroll(attempts - 1), 150);
    }
  };

  setTimeout(() => tryScroll(16), initialDelay);
}

/** Scroll target for back nav from subpages (less far than before). */
export function scrollToPendingHomeSection(sectionId: string) {
  const mobile = isMobileViewport();
  scrollToSectionById(sectionId, {
    offset: SECTION_SCROLL_OFFSET + SECTION_BACK_EXTRA_OFFSET,
    duration: SECTION_SCROLL_DURATION_MS,
    initialDelay: mobile ? 900 : 650,
  });
}
